import { createMruStore } from '@/lib/mruStore';

export interface RecentRecord {
  module: string;
  entityType: string;
  entityId: string;
  /** Business identity, e.g. 'ORD-001002'. Absent until the record has been read once. */
  label?: string;
  path: string;
}

/**
 * MRU store of records the user has opened, newest first, per tenant (see `createMruStore`).
 * Deduplicated on entity + id. Re-recording a record with a label it previously lacked upgrades
 * the entry in place, which is what happens when the page's data lands a moment after navigation;
 * a label it already had is never lost.
 */
const store = createMruStore<RecentRecord>({
  prefix: 'nexo.recentRecords',
  max: 20,
  isEntry: (x): x is RecentRecord => {
    const r = x as RecentRecord | null;
    return !!r && typeof r.entityId === 'string' && typeof r.entityType === 'string' && typeof r.path === 'string';
  },
  isSame: (a, b) => a.entityId === b.entityId && a.entityType === b.entityType,
  merge: (next, existing) => ({ ...next, label: next.label ?? existing?.label }),
});

export const recentRecordsStore = {
  /** Read the current list. Returns a stable reference between mutations. */
  getRecent: () => store.get(),
  record: (record: RecentRecord) => store.record(record),
  clear: () => store.clear(),
  subscribe: store.subscribe,
};
